import { Dispatch, FC, useEffect, useMemo, useState } from 'react';
import { connect, ConnectedProps } from 'react-redux';
import { useLocation, useNavigate } from 'react-router';
import { Action } from 'redux';

import { Earning } from '../../lib/models/fundamental/types';
import { RootState } from '../../lib/store';
import {
  searchEarnings as searchEarningsData,
  setEarningsCalendarSearchState as setEarningsCalendarSearchStateData,
} from '../../lib/store/fundamental/index';
import {
  EarningsCalendarSearchState,
  EarningsSymbolFilterEnum,
  FundamentalCall,
  SearchEarningsPayload,
} from '../../lib/store/fundamental/types';
import { isCallStatusPending } from '../../lib/util/error-handling/StatusByCallHelpers';

import { useSubPageTitle } from '../../hooks';
import {
  EarningsCalendarRow,
} from '../../shared_components/EarningsCalendar/components/EarningsTable/EarningsTable.types';
import { EARNINGS_CONTENT_PER_PAGE_LENGTH } from '../../shared_components/EarningsCalendar/EarningsCalendar.constants';
import {
  createSearchEarningsPayload,
  EarningsDateRange,
  getDefaultEarningsSearchDate,
  getDefaultOneMonthRange,
  isSearchEarningsQueryFetched,
} from '../../shared_components/EarningsCalendar/earningsCalendar.helpers';
import EarningsCalendarInternal from '../../shared_components/EarningsCalendar/EarningsCalendarInternal';
import { UI_ROUTES } from '../../util/routes';

import EarningsReportDetailsWrapper from './EarningsReportDetailsWrapper';

const EarningsCalendarInternalWrapper: FC<ReduxProps> = (props: ReduxProps): JSX.Element => {
  const {
    earnings,
    companyLogoAndName,
    isGettingEarning,
    isGettingCompanyLogoAndName,
    persistedSearchState,
    searchEarnings,
    setPersistedSearchState,
    resetFilters,
  } = props;

  const titleRef = useSubPageTitle<HTMLHeadingElement>();
  const navigate = useNavigate();
  const { state } = useLocation();
  const { startDate, endDate } = (state as { startDate?: string, endDate?: string }) || {};

  const [isLoadMoreClicked, setIsLoadMoreClicked] = useState<boolean>(false);
  const [selectedEarning, setSelectedEarning] = useState<Earning | null>(null);
  const defaultOneMonthRange = useMemo(() => getDefaultOneMonthRange(), []);

  const { searchValue, activeSymbols } = persistedSearchState;
  const range: EarningsDateRange = persistedSearchState.range ?? {
    startDate: startDate ?? defaultOneMonthRange.startDate,
    endDate: endDate ?? defaultOneMonthRange.endDate,
    key: 'selection',
  };
  const { content } = earnings;

  const rows = useMemo((): EarningsCalendarRow[] => content.map(earning => ({
    earning,
    logo: companyLogoAndName[earning.symbol]?.logo,
    logoUrl: companyLogoAndName[earning.symbol]?.url,
    companyName: companyLogoAndName[earning.symbol]?.name,
  })), [content, companyLogoAndName]);

  // Seed the resolved initial range into Redux once, so this screen and the Discover widget
  // derive the same range and later date changes have a persisted base to overwrite.
  useEffect(() => {
    if (persistedSearchState.range) return;

    setPersistedSearchState({
      range: {
        startDate: String(range.startDate),
        endDate: String(range.endDate),
        key: range.key ?? 'selection',
      },
    });
  }, []);

  // The period is scoped to this screen - leaving it returns the calendar to the default.
  useEffect(() => () => resetFilters(), []);

  const queryPayload = createSearchEarningsPayload(range.startDate, range.endDate);

  // Single fetch path: whenever the effective query differs from what was last requested,
  // fetch page 0. Skipping when it already matches preserves Load-more progress across
  // remounts; the pending guard stops a duplicate request while one is in flight.
  useEffect(() => {
    const isQueryFetched = isSearchEarningsQueryFetched(persistedSearchState.lastFetchedQuery, queryPayload);
    if (isQueryFetched && (content.length > 0 || isGettingEarning)) return;

    setIsLoadMoreClicked(false);
    searchEarnings(queryPayload);
  }, [queryPayload.startDate, queryPayload.endDate]);

  const handleDateChange = (value: EarningsDateRange) => {
    setPersistedSearchState({
      range: {
        startDate: String(value.startDate),
        endDate: String(value.endDate),
        key: value.key ?? 'selection',
      },
    });
  };

  const handleLoadMore = () => {
    const nextPage = Math.floor(content.length / EARNINGS_CONTENT_PER_PAGE_LENGTH);

    searchEarnings(createSearchEarningsPayload(
      range.startDate || getDefaultEarningsSearchDate(),
      range.endDate || getDefaultEarningsSearchDate(),
      nextPage,
    ));
    setIsLoadMoreClicked(true);
  };

  const handleGoBack = () => navigate(UI_ROUTES.discover);

  // A page-0 fetch replaces the whole result set, so while one is in flight the content in
  // Redux may belong to a different query - show the skeleton rather than flashing stale rows.
  const showSkeleton = (isGettingEarning && !isLoadMoreClicked)
    || (!content.length && isGettingCompanyLogoAndName && !isLoadMoreClicked);

  return (
    <>
      <EarningsCalendarInternal
        rows={rows}
        totalElements={earnings.totalElements}
        range={range}
        activeSymbols={activeSymbols}
        searchValue={searchValue}
        isPending={isGettingEarning}
        showSkeleton={showSkeleton}
        canLoadMore={content.length > 0 && content.length < earnings.totalElements}
        onDateChange={handleDateChange}
        onLoadMore={handleLoadMore}
        onEarningSelect={setSelectedEarning}
        isAce
        titleRef={titleRef}
        onGoBack={handleGoBack}
      />
      {selectedEarning && (
        <EarningsReportDetailsWrapper earning={selectedEarning} onClose={() => setSelectedEarning(null)} />
      )}
    </>
  );
};

const mapStateToProps = (state: RootState) => ({
  earnings: state.fundamental.earningsSearchResponse,
  companyLogoAndName: state.fundamental.companyLogoAndName,
  isGettingEarning: isCallStatusPending(FundamentalCall.searchEarnings, state.fundamental.statusByCall),
  isGettingCompanyLogoAndName: isCallStatusPending(
    FundamentalCall.getCompanyLogoAndName, state.fundamental.statusByCall,
  ),
  persistedSearchState: state.fundamental.earningsCalendarSearchState,
});

const mapDispatchToProps = (dispatch: Dispatch<Action>) => ({
  searchEarnings: (payload: SearchEarningsPayload) => dispatch(searchEarningsData(payload)),
  setPersistedSearchState: (payload: Partial<EarningsCalendarSearchState>) =>
    dispatch(setEarningsCalendarSearchStateData(payload)),
  resetFilters: () => dispatch(setEarningsCalendarSearchStateData({
    range: null,
    searchValue: '',
    activeSymbols: [],
    selectedSymbolFilterId: EarningsSymbolFilterEnum.AllSymbols,
  })),
});

const connector = connect(mapStateToProps, mapDispatchToProps);
type ReduxProps = ConnectedProps<typeof connector>;

export default connector(EarningsCalendarInternalWrapper);
